import { DecimalPipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ApiClientService } from '../core/api-client.service';
import { I18nService } from '../core/i18n.service';
import { SeoService } from '../core/seo.service';
import { PageDataService } from '../core/page-data.service';

interface HistoryItem {
  listingID: number;
  title: string;
  slug: string;
  categoryTitle?: string | null;
  completedAt?: string | null;
  rating?: number | null;
  comment?: string | null;
}

@Component({
  selector: 'app-seller-history-page',
  imports: [DecimalPipe, RouterLink],
  template: `
    <main class="seller-history-page">
      <a class="back" [routerLink]="i18n.routePath('seller', { slug: slug })">← Falcı profiline dön</a>
      <p class="eyebrow">{{ sellerName() || 'Falcı' }}</p>
      <h1>Geçmiş Fallar</h1>
      <p class="lead">Bu falcının daha önce yorumladığı fallar.</p>
      @if (loading()) { <p class="state">Fallara bakılıyor...</p> }
      @if (!loading() && !items().length) { <p class="state">Henüz tamamlanmış fal bulunmuyor.</p> }
      <div class="history-list">
        @for (item of items(); track item.listingID + '-' + $index) {
          <a class="history-item" [routerLink]="i18n.routePath('listing', { slug: item.slug })">
            <small>{{ item.categoryTitle || 'Fal' }}</small>
            <strong>{{ item.title }}</strong>
            @if (item.rating) { <span>★ {{ item.rating | number:'1.1-2' }}/5</span> }
            @if (item.comment) { <p>{{ item.comment }}</p> }
            <small>{{ item.completedAt || '' }}</small>
          </a>
        }
      </div>
      @if (items().length) {
        <nav class="pagination" aria-label="Geçmiş fallar sayfalaması">
          <button type="button" [disabled]="page() <= 1 || loading()" (click)="load(page() - 1)">Önceki</button>
          <span>Sayfa {{ page() }}</span>
          <button type="button" [disabled]="!hasMore() || loading()" (click)="load(page() + 1)">Sonraki</button>
        </nav>
      }
    </main>
  `,
  styles: [`
    .seller-history-page{margin:0 auto;max-width:900px;padding:clamp(24px,5vw,72px) 20px}.back{color:var(--accent);font-weight:900;text-decoration:none}.eyebrow{color:var(--accent);font-size:.78rem;font-weight:950;letter-spacing:.14em;margin:36px 0 8px;text-transform:uppercase}.seller-history-page h1{font-family:Georgia,serif;font-size:clamp(2.5rem,6vw,5rem);letter-spacing:-.06em;margin:0}.lead,.state{color:var(--muted);font-size:1.05rem;line-height:1.6}.history-list{display:grid;gap:14px;margin-top:32px}.history-item{background:var(--card);border:1px solid var(--border);border-radius:22px;color:inherit;display:grid;gap:7px;padding:20px;text-decoration:none}.history-item strong{font-family:Georgia,serif;font-size:1.45rem}.history-item span{color:var(--accent);font-weight:900}.history-item p{color:var(--muted);margin:.2rem 0;line-height:1.55}.history-item small{color:var(--muted)}.pagination{align-items:center;display:flex;gap:12px;justify-content:center;margin:28px 0}.pagination button{background:var(--accent);border:0;border-radius:999px;color:var(--accent-contrast,#fff);cursor:pointer;font:inherit;font-weight:900;padding:11px 16px}.pagination button:disabled{cursor:not-allowed;opacity:.45}
  `]
})
export class SellerHistoryPage implements OnInit {
  protected readonly i18n = inject(I18nService);
  private readonly api = inject(ApiClientService);
  private readonly route = inject(ActivatedRoute);
  private readonly seo = inject(SeoService);
  private readonly pageData = inject(PageDataService);
  protected readonly slug = this.route.snapshot.paramMap.get('slug') ?? '';
  protected readonly items = signal<HistoryItem[]>([]);
  protected readonly page = signal(1);
  protected readonly hasMore = signal(false);
  protected readonly loading = signal(false);
  protected readonly sellerName = signal('');
  private sellerID = 0;

  ngOnInit(): void { void this.initialize(); }

  protected async load(page: number): Promise<void> {
    if (this.loading()) return;
    this.loading.set(true);
    try {
      const response: any = await firstValueFrom(this.api.post('/next/seller-history', { sellerID: this.sellerID, slug: this.slug, page, limit: 8, lang: this.i18n.lang() }));
      const data = response?.data ?? response ?? {};
      if (!this.sellerID) this.sellerID = Number(data.sellerID || 0);
      if (!this.sellerName() && data.sellerName) this.sellerName.set(String(data.sellerName));
      this.items.set(Array.isArray(data.items) ? data.items : []);
      this.page.set(Number(data.page || page));
      this.hasMore.set(Boolean(data.hasMore));
    } finally { this.loading.set(false); }
  }

  private async initialize(): Promise<void> {
    this.loading.set(true);
    try {
      const bootstrap = this.pageData.get<any>('sellerPage')?.data ?? null;
      const bootstrapSeller = bootstrap?.seller ?? {};
      this.sellerID = Number(bootstrapSeller.sellerID || bootstrapSeller.id || this.sellerID || 0);
      this.sellerName.set(String(bootstrapSeller.sellerName || bootstrapSeller.shopName || this.sellerName() || ''));
      const pastReadings = Array.isArray(bootstrap?.pastReadings) ? bootstrap.pastReadings : [];
      if (pastReadings.length) {
        this.items.set(pastReadings.map((item: any) => ({
          listingID: Number(item.listingID || 0), title: String(item.title || ''), slug: String(item.slug || ''),
          categoryTitle: item.categoryTitle ?? null, completedAt: item.completedAt ?? null,
          rating: item.rating === null || item.rating === undefined ? null : Number(item.rating), comment: null
        })));
        this.hasMore.set(false);
        this.seo.setPage({ title: `${this.sellerName() || 'Falcı'} · Geçmiş Fallar`, description: `${this.sellerName() || 'Falcı'} tarafından yorumlanan geçmiş fallar.`, path: this.i18n.routePath('sellerHistory', { slug: this.slug }) });
        return;
      }
      await this.load(1);
      this.seo.setPage({ title: `${this.sellerName() || 'Falcı'} · Geçmiş Fallar`, description: `${this.sellerName() || 'Falcı'} tarafından yorumlanan geçmiş fallar.`, path: this.i18n.routePath('sellerHistory', { slug: this.slug }) });
    } catch { this.items.set([]); } finally { this.loading.set(false); }
  }
}
